/**
 * Iraq province codes drift across two formats in the Boxy API:
 *
 *   - Legacy 3-letter (regions.province_code): BGD, ARB, BAS, ...
 *   - Canonical 2-letter (ISO 3166-2:IQ subdivision): BG, AR, BA, ...
 *
 * Merchant directive 2026-05-12: 2-letter codes are canonical for new
 * data. The /evaluate-shipping-rules endpoint and shipping-rule editor
 * must emit 2-letter codes. Old 3-letter values still arriving from
 * the regions endpoint are normalised here so the dashboard speaks one
 * format to the backend even though the source data is in the other.
 *
 * This helper is the TypeScript mirror of the backend
 * `App\Utils\ShippingRule\ProvinceCodeNormalizer`. Keep both in sync
 * when adding provinces.
 */

const LEGACY_TO_CANONICAL: Record<string, string> = {
  BGD: 'BG', // Baghdad
  ARB: 'AR', // Erbil (Arbil)
  NJF: 'NA', // Najaf
  BBL: 'BB', // Babil (Babel)
  KOT: 'WA', // Kut (Wasit)
  DYL: 'DI', // Diyala
  DWN: 'QA', // Diwaniyah (Al Qadisiyyah)
  MOS: 'NI', // Mosul (Ninawa)
  BAS: 'BA', // Basra
  SMH: 'SU', // Sulaymaniyah
  DOH: 'DA', // Duhok (Dahuk)
  SAH: 'SD', // Saladin
  ANB: 'AN', // Al Anbar
  NAS: 'DQ', // Nasiriyah (Dhi Qar)
  KRK: 'KI', // Kirkuk
  SAM: 'MU', // Samawah (Al Muthanna)
  AMA: 'MA', // Amarah (Maysan)
  KRB: 'KA', // Karbala
};

/**
 * The 18 real Iraqi governorates, in canonical 2-letter form.
 *
 * The `regions` table carries far more distinct `province_code` values than
 * this (~1767 locally, ~600+ on staging): seed data stuffs faker city names
 * into the column, and those rows have perfectly non-null `province`/
 * `province_en_name`, so a "has a name" heuristic does not filter them out.
 * Anything whose normalized code is not in this set is not a province.
 */
export const CANONICAL_PROVINCE_CODES: ReadonlySet<string> = new Set(
  Object.values(LEGACY_TO_CANONICAL),
);

export function isCanonicalProvinceCode(code: string | null | undefined): boolean {
  const normalized = normalizeProvinceCode(code);
  return normalized !== undefined && CANONICAL_PROVINCE_CODES.has(normalized);
}

export function normalizeProvinceCode(code: string | null | undefined): string | undefined {
  if (code === null || code === undefined) return undefined;
  const trimmed = code.trim().toUpperCase();
  if (trimmed === '') return undefined;
  return LEGACY_TO_CANONICAL[trimmed] ?? trimmed;
}

export function normalizeProvinceCodes(codes: Array<string | null | undefined>): string[] {
  return codes
    .map(normalizeProvinceCode)
    .filter((c): c is string => typeof c === 'string' && c !== '');
}
